import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Filter, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import {
  reportsService,
  type DeliveryUserOrderReportRow,
  type GeneralOrderReportRow,
  type OrderReportFilterOptions,
  type OrderReportFilters,
  type OrderReportResult,
  type OrderReportSort,
  type OrderReportType,
} from '../../../services/reports.service'
import { GlobalFilterBar } from '../../ui/GlobalFilterBar'
import { Button } from '../../ui/button'
import { GlobalPaginator } from '../../ui/global-paginator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table'
import { TableSkeleton } from '../../ui/table-skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs'

const DEFAULT_FILTERS: Record<OrderReportType, OrderReportFilters> = {
  general: {},
  'delivery-users': { sort_by: 'orders' },
}
const REPORT_TABLE_HEAD_CLASS =
  'h-auto px-6 py-3 text-xs font-medium uppercase tracking-wider text-table-header-foreground'
const REPORT_TABLE_CELL_CLASS = 'px-6 py-4 text-table-foreground'

export interface AppliedOrderReportContext {
  type: OrderReportType
  filters: OrderReportFilters
}

interface PedidosTabProps {
  activeReport: OrderReportType
  onActiveReportChange: (report: OrderReportType) => void
  onAppliedContextChange: (context: AppliedOrderReportContext) => void
}

export function PedidosTab({
  activeReport,
  onActiveReportChange,
  onAppliedContextChange,
}: PedidosTabProps) {
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS)
  const [page, setPage] = useState(1)
  const currentDraft = draftFilters[activeReport]
  const currentApplied = appliedFilters[activeReport]
  const queryFilters = useMemo(
    () => ({ ...currentApplied, page }),
    [currentApplied, page],
  )
  const optionsQuery = useQuery({
    queryKey: ['reports', 'orders', 'filter-options'],
    queryFn: () => reportsService.getOrderFilterOptions(),
    staleTime: 5 * 60 * 1000,
  })
  const reportQuery = useQuery({
    queryKey: ['reports', 'orders', activeReport, queryFilters],
    queryFn: () =>
      reportsService.getOrderReport<
        GeneralOrderReportRow | DeliveryUserOrderReportRow
      >(activeReport, queryFilters),
    placeholderData: (previous) =>
      previous?.report === activeReport ? previous : undefined,
  })

  useEffect(() => {
    onAppliedContextChange({ type: activeReport, filters: currentApplied })
  }, [activeReport, currentApplied, onAppliedContextChange])

  const updateDraft = (changes: Partial<OrderReportFilters>) => {
    setDraftFilters((current) => ({
      ...current,
      [activeReport]: { ...current[activeReport], ...changes },
    }))
  }

  const applyFilters = () => {
    setAppliedFilters((current) => ({
      ...current,
      [activeReport]: { ...currentDraft },
    }))
    setPage(1)
  }

  const clearFilters = () => {
    setDraftFilters((current) => ({
      ...current,
      [activeReport]: { ...DEFAULT_FILTERS[activeReport] },
    }))
    setAppliedFilters((current) => ({
      ...current,
      [activeReport]: { ...DEFAULT_FILTERS[activeReport] },
    }))
    setPage(1)
  }

  const changeReport = (value: string) => {
    onActiveReportChange(value as OrderReportType)
    setPage(1)
  }

  return (
    <TabsContent value="pedidos" className="space-y-5">
      <Tabs
        value={activeReport}
        onValueChange={changeReport}
        className="space-y-5"
      >
        <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-slate-100 p-1">
          <TabsTrigger value="general" className="text-xs sm:text-sm">
            Pedidos
          </TabsTrigger>
          <TabsTrigger value="delivery-users" className="text-xs sm:text-sm">
            Por repartidor
          </TabsTrigger>
        </TabsList>

        <GlobalFilterBar
          show={['dates']}
          values={{
            startDate: String(currentDraft.date_from ?? ''),
            endDate: String(currentDraft.date_to ?? ''),
          }}
          onChange={(changes) =>
            updateDraft({
              ...(changes.startDate !== undefined
                ? { date_from: changes.startDate || undefined }
                : {}),
              ...(changes.endDate !== undefined
                ? { date_to: changes.endDate || undefined }
                : {}),
            })
          }
          onClearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters(activeReport, currentDraft)}
        >
          {activeReport === 'general' ? (
            <Field label="Estado">
              <Select value="all" disabled>
                <SelectTrigger aria-label="Estado">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  {(
                    optionsQuery.data?.statuses ?? [
                      { value: 'all', label: 'Todos' },
                    ]
                  ).map((option) => (
                    <SelectItem
                      key={String(option.value)}
                      value={String(option.value)}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          ) : (
            <OptionsSelect
              label="Ordenar por"
              value={String(currentDraft.sort_by ?? 'orders')}
              options={optionsQuery.data?.sort_options}
              onChange={(value) =>
                updateDraft({ sort_by: value as OrderReportSort })
              }
            />
          )}
          <div className="flex items-end gap-2">
            <Button type="button" onClick={applyFilters}>
              <Filter className="mr-2 h-4 w-4" />
              Aplicar filtros
            </Button>
            {optionsQuery.isLoading && (
              <span className="flex items-center gap-1 pb-2 text-xs text-slate-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Catálogos
              </span>
            )}
          </div>
        </GlobalFilterBar>

        <OrderResults
          type={activeReport}
          result={reportQuery.data}
          isLoading={reportQuery.isLoading}
          isFetching={reportQuery.isFetching}
          error={reportQuery.error}
          onPageChange={setPage}
        />
      </Tabs>
    </TabsContent>
  )
}

function OrderResults({
  type,
  result,
  isLoading,
  isFetching,
  error,
  onPageChange,
}: {
  type: OrderReportType
  result?: OrderReportResult<GeneralOrderReportRow | DeliveryUserOrderReportRow>
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  onPageChange: (page: number) => void
}) {
  const headers =
    type === 'general'
      ? ['Ticket', 'Sucursal', 'Usuario', 'Descuento', 'Importe']
      : [
          'Repartidor',
          'No. pedidos',
          'No. pedidos cancelados',
          'Promedio',
          'Descuento',
        ]

  if (isLoading) {
    return (
      <TableSkeleton
        headers={headers}
        columns={['text', 'text', 'text', 'text', 'text']}
        rows={10}
      />
    )
  }
  if (error) {
    return (
      <ReportState
        icon={<AlertCircle className="h-6 w-6 text-red-500" />}
        message="No fue posible cargar el reporte. Intenta nuevamente."
      />
    )
  }
  if (!result || result.rows.length === 0) {
    return <ReportState message="No hay pedidos para los filtros aplicados." />
  }

  return (
    <div className="border-border bg-table-bg text-table-foreground overflow-hidden rounded-lg border shadow-sm">
      <div className="border-border flex items-center justify-between border-b px-6 py-4">
        <div>
          <h3 className="text-table-foreground font-semibold">Resultados</h3>
          <p className="text-muted-foreground text-xs">
            {result.pagination.total}{' '}
            {type === 'general'
              ? 'pedidos filtrados'
              : 'repartidores filtrados'}
          </p>
        </div>
        {isFetching && (
          <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
        )}
      </div>
      <div className="overflow-x-auto">
        <Table className="min-w-max">
          <TableHeader className="border-border bg-table-header-bg text-table-header-foreground border-b">
            <TableRow className="hover:bg-transparent">
              {headers.map((header, index) => (
                <TableHead
                  key={header}
                  className={`${REPORT_TABLE_HEAD_CLASS} ${index >= 3 || (type === 'delivery-users' && index >= 1) ? 'text-right' : ''}`}
                >
                  {header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          {type === 'general' ? (
            <GeneralOrderRows
              rows={result.rows as GeneralOrderReportRow[]}
              result={result}
            />
          ) : (
            <DeliveryUserRows
              rows={result.rows as DeliveryUserOrderReportRow[]}
              result={result}
            />
          )}
        </Table>
      </div>
      <GlobalPaginator
        totalItems={result.pagination.total}
        itemsPerPage={result.pagination.per_page}
        currentPage={result.pagination.current_page}
        from={result.pagination.from ?? 0}
        to={result.pagination.to ?? 0}
        onPageChange={onPageChange}
      />
    </div>
  )
}

function GeneralOrderRows({
  rows,
  result,
}: {
  rows: GeneralOrderReportRow[]
  result: OrderReportResult<GeneralOrderReportRow | DeliveryUserOrderReportRow>
}) {
  return (
    <>
      <TableBody className="[&_tr]:border-border">
        {rows.map((row, index) => (
          <TableRow
            key={`${row.ticket}-${index}`}
            className="hover:bg-table-row-hover"
          >
            <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-medium`}>
              {row.ticket}
            </TableCell>
            <TableCell className={REPORT_TABLE_CELL_CLASS}>
              {row.branch}
            </TableCell>
            <TableCell className={REPORT_TABLE_CELL_CLASS}>
              {row.user}
            </TableCell>
            <MoneyCell value={row.discount} />
            <MoneyCell value={row.amount} strong />
          </TableRow>
        ))}
      </TableBody>
      <TableFooter className="border-border bg-table-header-bg text-table-foreground border-t font-semibold">
        <TableRow className="hover:bg-transparent">
          <TableCell className={REPORT_TABLE_CELL_CLASS}>
            TOTAL GENERAL
          </TableCell>
          <TableCell className={REPORT_TABLE_CELL_CLASS}>
            Registros: {result.totals.record_count}
          </TableCell>
          <TableCell className={REPORT_TABLE_CELL_CLASS} />
          <MoneyCell value={result.totals.discount} strong />
          <MoneyCell value={result.totals.amount} strong />
        </TableRow>
      </TableFooter>
    </>
  )
}

function DeliveryUserRows({
  rows,
  result,
}: {
  rows: DeliveryUserOrderReportRow[]
  result: OrderReportResult<GeneralOrderReportRow | DeliveryUserOrderReportRow>
}) {
  return (
    <>
      <TableBody className="[&_tr]:border-border">
        {rows.map((row) => (
          <TableRow
            key={row.delivery_user}
            className="hover:bg-table-row-hover"
          >
            <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-medium`}>
              {row.delivery_user}
            </TableCell>
            <NumberCell value={row.order_count} />
            <NumberCell value={row.cancelled_count} />
            <MoneyCell value={row.average} />
            <MoneyCell value={row.discount} />
          </TableRow>
        ))}
      </TableBody>
      <TableFooter className="border-border bg-table-header-bg text-table-foreground border-t font-semibold">
        <TableRow className="hover:bg-transparent">
          <TableCell className={REPORT_TABLE_CELL_CLASS}>
            TOTAL GENERAL · Repartidores: {result.totals.record_count}
          </TableCell>
          <NumberCell value={result.totals.order_count ?? 0} strong />
          <NumberCell value={result.totals.cancelled_count ?? 0} strong />
          <MoneyCell value={result.totals.average} strong />
          <MoneyCell value={result.totals.discount} strong />
        </TableRow>
      </TableFooter>
    </>
  )
}

function OptionsSelect({
  label,
  value,
  options = [],
  onChange,
}: {
  label: string
  value: string
  options?: OrderReportFilterOptions['sort_options']
  onChange: (value: string) => void
}) {
  return (
    <Field label={label}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={String(option.value)}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  )
}

function MoneyCell({
  value,
  strong = false,
}: {
  value: string | number | null | undefined
  strong?: boolean
}) {
  return (
    <TableCell
      className={`${REPORT_TABLE_CELL_CLASS} text-right tabular-nums ${strong ? 'font-semibold' : ''}`}
    >
      {formatMoney(value)}
    </TableCell>
  )
}

function NumberCell({
  value,
  strong = false,
}: {
  value: number
  strong?: boolean
}) {
  return (
    <TableCell
      className={`${REPORT_TABLE_CELL_CLASS} text-right tabular-nums ${strong ? 'font-semibold' : ''}`}
    >
      {value.toLocaleString('es-MX')}
    </TableCell>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      {children}
    </div>
  )
}

function ReportState({ icon, message }: { icon?: ReactNode; message: string }) {
  return (
    <div className="flex min-h-52 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
      {icon}
      <span>{message}</span>
    </div>
  )
}

function hasActiveFilters(type: OrderReportType, filters: OrderReportFilters) {
  const defaults = DEFAULT_FILTERS[type]
  const keys = new Set([...Object.keys(defaults), ...Object.keys(filters)])

  return Array.from(keys).some((key) => filters[key] !== defaults[key])
}

function formatMoney(value: string | number | null | undefined) {
  return Number(value ?? 0).toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
  })
}
